// Query key factory for TanStack Query.
// Keys mirror the API resource structure: ['tenant', slug, 'resource', ...identifiers]
// This enables precise cache invalidation and consistent key patterns.

export const queryKeys = {
  /** Invalidate everything for a tenant. */
  tenant: (slug: string) => ['tenant', slug] as const,

  // --- Framework Configuration (Group 08) ---
  frameworkConfig: {
    // Taxonomy
    taxonomy: (slug: string) => ['tenant', slug, 'framework-config', 'taxonomy'] as const,
    taxonomyVersions: (slug: string) =>
      ['tenant', slug, 'framework-config', 'taxonomy', 'versions'] as const,
    taxonomyVersion: (slug: string, versionId: string) =>
      ['tenant', slug, 'framework-config', 'taxonomy', 'versions', versionId] as const,

    // Likelihood
    likelihood: (slug: string) => ['tenant', slug, 'framework-config', 'likelihood'] as const,
    likelihoodVersions: (slug: string) =>
      ['tenant', slug, 'framework-config', 'likelihood', 'versions'] as const,

    // Impact tables
    impactTables: (slug: string) => ['tenant', slug, 'framework-config', 'impact-tables'] as const,
    impactTableVersions: (slug: string, tableId: string) =>
      ['tenant', slug, 'framework-config', 'impact-tables', tableId, 'versions'] as const,

    // Scoring grid
    scoringGrid: (slug: string) => ['tenant', slug, 'framework-config', 'scoring-grid'] as const,

    // Risk appetite
    appetite: (slug: string) => ['tenant', slug, 'framework-config', 'appetite'] as const,
    appetiteNode: (slug: string, nodeId: string) =>
      ['tenant', slug, 'framework-config', 'appetite', nodeId] as const,

    // Evidence tags
    evidenceTags: (slug: string) => ['tenant', slug, 'framework-config', 'evidence-tags'] as const,

    // Assessment thresholds
    assessmentThresholds: (slug: string) =>
      ['tenant', slug, 'framework-config', 'assessment-thresholds'] as const,
  },

  // --- Registers (Group 01) ---
  registers: {
    all: (slug: string) => ['tenant', slug, 'registers'] as const,
    tree: (slug: string) => ['tenant', slug, 'registers', 'tree'] as const,
    detail: (slug: string, id: string) => ['tenant', slug, 'registers', id] as const,
    children: (slug: string, id: string) => ['tenant', slug, 'registers', id, 'children'] as const,
  },

  // --- Scoring Lookups (non-admin, read-only) ---
  scoring: {
    likelihoodLevels: (slug: string) => ['tenant', slug, 'scoring', 'likelihood-levels'] as const,
    impactTables: (slug: string) => ['tenant', slug, 'scoring', 'impact-tables'] as const,
    appetite: (slug: string, nodeId: string) =>
      ['tenant', slug, 'scoring', 'appetite', nodeId] as const,
    taxonomyNodes: (slug: string) => ['tenant', slug, 'scoring', 'taxonomy-nodes'] as const,
  },

  // --- Risks (Group 02) ---
  risks: {
    all: (slug: string) => ['tenant', slug, 'risks'] as const,
    detail: (slug: string, id: string) => ['tenant', slug, 'risks', id] as const,
    impactAssessments: (slug: string, id: string) =>
      ['tenant', slug, 'risks', id, 'impact-assessments'] as const,
    controls: (slug: string, id: string) => ['tenant', slug, 'risks', id, 'controls'] as const,
    actions: (slug: string, id: string) => ['tenant', slug, 'risks', id, 'actions'] as const,
    events: (slug: string, id: string) => ['tenant', slug, 'risks', id, 'events'] as const,
    evidence: (slug: string, id: string) => ['tenant', slug, 'risks', id, 'evidence'] as const,
    history: (slug: string, id: string) => ['tenant', slug, 'risks', id, 'history'] as const,
    children: (slug: string, id: string) => ['tenant', slug, 'risks', id, 'children'] as const,
    childScoringStatus: (slug: string, id: string) =>
      ['tenant', slug, 'risks', id, 'child-scoring-status'] as const,
    eligibleChildren: (slug: string, id: string) =>
      ['tenant', slug, 'risks', id, 'eligible-children'] as const,
  },

  // --- Controls (Group 03) ---
  controls: {
    all: (slug: string) => ['tenant', slug, 'controls'] as const,
    detail: (slug: string, id: string) => ['tenant', slug, 'controls', id] as const,
    assessments: (slug: string, id: string) =>
      ['tenant', slug, 'controls', id, 'assessments'] as const,
    assessmentHistory: (slug: string, id: string) =>
      ['tenant', slug, 'controls', id, 'assessment-history'] as const,
    kpis: (slug: string, id: string) =>
      ['tenant', slug, 'controls', id, 'kpis'] as const,
    risks: (slug: string, id: string) =>
      ['tenant', slug, 'controls', id, 'risks'] as const,
    actions: (slug: string, id: string) =>
      ['tenant', slug, 'controls', id, 'actions'] as const,
    frameworks: (slug: string, id: string) =>
      ['tenant', slug, 'controls', id, 'frameworks'] as const,
    evidence: (slug: string, id: string) =>
      ['tenant', slug, 'controls', id, 'evidence'] as const,
    history: (slug: string, id: string) =>
      ['tenant', slug, 'controls', id, 'history'] as const,
  },

  // --- Actions (Group 04) ---
  actions: {
    all: (slug: string) => ['tenant', slug, 'actions'] as const,
    detail: (slug: string, id: string) => ['tenant', slug, 'actions', id] as const,
    updates: (slug: string, id: string) => ['tenant', slug, 'actions', id, 'updates'] as const,
    controls: (slug: string, id: string) => ['tenant', slug, 'actions', id, 'controls'] as const,
    events: (slug: string, id: string) => ['tenant', slug, 'actions', id, 'events'] as const,
    risks: (slug: string, id: string) => ['tenant', slug, 'actions', id, 'risks'] as const,
    evidence: (slug: string, id: string) => ['tenant', slug, 'actions', id, 'evidence'] as const,
    history: (slug: string, id: string) => ['tenant', slug, 'actions', id, 'history'] as const,
  },

  // --- Events (Group 05) ---
  events: {
    all: (slug: string) => ['tenant', slug, 'events'] as const,
    detail: (slug: string, id: string) => ['tenant', slug, 'events', id] as const,
    types: (slug: string) => ['tenant', slug, 'events', 'types'] as const,
    risks: (slug: string, id: string) => ['tenant', slug, 'events', id, 'risks'] as const,
    actions: (slug: string, id: string) => ['tenant', slug, 'events', id, 'actions'] as const,
    evidence: (slug: string, id: string) => ['tenant', slug, 'events', id, 'evidence'] as const,
  },

  // --- Categories (Group 06) ---
  categories: {
    all: (slug: string, type: string) =>
      ['tenant', slug, 'categories', type] as const,
    list: (slug: string, type: string, search?: string) =>
      ['tenant', slug, 'categories', type, 'list', { search }] as const,
    tree: (slug: string, type: string) =>
      ['tenant', slug, 'categories', type, 'tree'] as const,
    detail: (slug: string, type: string, id: string) =>
      ['tenant', slug, 'categories', type, id] as const,
    children: (slug: string, type: string, id: string) =>
      ['tenant', slug, 'categories', type, id, 'children'] as const,
  },

  // --- Compliance Frameworks (Group 07) ---
  complianceFrameworks: {
    all: (slug: string) => ['tenant', slug, 'compliance-frameworks'] as const,
    list: (slug: string, filters?: { search?: string; category_id?: string }) =>
      ['tenant', slug, 'compliance-frameworks', 'list', filters] as const,
    detail: (slug: string, id: string) => ['tenant', slug, 'compliance-frameworks', id] as const,
    items: (slug: string, id: string) =>
      ['tenant', slug, 'compliance-frameworks', id, 'items'] as const,
    unmappedItems: (slug: string, id: string) =>
      ['tenant', slug, 'compliance-frameworks', id, 'items', 'unmapped'] as const,
    itemDetail: (slug: string, frameworkId: string, itemId: string) =>
      ['tenant', slug, 'compliance-frameworks', frameworkId, 'items', itemId] as const,
    library: (slug: string) =>
      ['tenant', slug, 'compliance-frameworks', 'library'] as const,
    libraryDetail: (slug: string, libraryId: string) =>
      ['tenant', slug, 'compliance-frameworks', 'library', libraryId] as const,
  },

  // --- Users & Roles (Group 09) ---
  users: {
    all: (slug: string) => ['tenant', slug, 'users'] as const,
    list: (slug: string, filters?: { status?: string; search?: string; include_api?: boolean }) =>
      ['tenant', slug, 'users', 'list', filters] as const,
    detail: (slug: string, id: string) => ['tenant', slug, 'users', id] as const,
    me: (slug: string) => ['tenant', slug, 'users', 'me'] as const,
    myApiKey: (slug: string) => ['tenant', slug, 'users', 'me', 'api-key'] as const,
  },
  roles: {
    all: (slug: string) => ['tenant', slug, 'roles'] as const,
    list: (slug: string, search?: string) =>
      ['tenant', slug, 'roles', 'list', { search }] as const,
    detail: (slug: string, id: string) => ['tenant', slug, 'roles', id] as const,
    members: (slug: string, id: string) =>
      ['tenant', slug, 'roles', id, 'members'] as const,
    grants: (slug: string, id: string) =>
      ['tenant', slug, 'roles', id, 'grants'] as const,
  },

  // --- API Users (Group 15) ---
  apiUsers: {
    all: (slug: string) => ['tenant', slug, 'api-users'] as const,
  },

  // --- Ownership Roles (Group 14) ---
  ownershipRoles: {
    all: (slug: string) => ['tenant', slug, 'ownership-roles'] as const,
  },

  // --- Grants (Group 13) ---
  grants: {
    all: (slug: string) => ['tenant', slug, 'grants'] as const,
    list: (slug: string, filters?: { role_id?: string; domain?: string }) =>
      ['tenant', slug, 'grants', 'list', filters] as const,
  },

  // --- Tenant Settings (Group 10) ---
  tenantSettings: {
    context: (slug: string) => ['tenant', slug, 'settings', 'context'] as const,
    branding: (slug: string) => ['tenant', slug, 'settings', 'branding'] as const,
  },

  // --- Webhooks (Group 11) ---
  webhooks: {
    all: (slug: string) => ['tenant', slug, 'webhooks'] as const,
    detail: (slug: string, id: string) => ['tenant', slug, 'webhooks', id] as const,
    deliveryLog: (slug: string, id: string) =>
      ['tenant', slug, 'webhooks', id, 'deliveries'] as const,
  },

  // --- Dashboard & Reporting (Group 12) ---
  dashboard: {
    summary: (slug: string) => ['tenant', slug, 'dashboard'] as const,
  },
  // --- Import (Group 16) ---
  imports: {
    all: (slug: string) => ['tenant', slug, 'imports'] as const,
    detail: (slug: string, importId: string) =>
      ['tenant', slug, 'imports', importId] as const,
  },

  // --- Entitlements (Group 17) ---
  entitlements: (slug: string) => ['tenant', slug, 'entitlements'] as const,

  // --- Node Policies (Group 19) ---
  nodePolicies: {
    list: (slug: string, nodeType: string) =>
      ['tenant', slug, 'node-policies', nodeType] as const,
    effective: (slug: string, nodeType: string, nodeId: string) =>
      ['tenant', slug, 'node-policies', nodeType, nodeId] as const,
  },

  // --- Platform Settings (Group 18) ---
  platform: {
    subscription: (slug: string) => ['tenant', slug, 'platform', 'subscription'] as const,
    tenantConfig: (slug: string) => ['tenant', slug, 'platform', 'tenant-config'] as const,
    idpProviders: (slug: string) => ['tenant', slug, 'platform', 'idp-providers'] as const,
    rebindStatus: (slug: string) => ['tenant', slug, 'platform', 'rebind-status'] as const,
  },

  reporting: {
    riskHeatmap: (slug: string, filters?: { excludeChildren?: boolean; taxonomyNodeId?: string }) =>
      ['tenant', slug, 'reporting', 'risk-heatmap', filters] as const,
    eventsLastPeriod: (slug: string, filters?: { days?: number }) =>
      ['tenant', slug, 'reporting', 'events-last-period', filters] as const,
    controlAssessments: (slug: string, filters?: { days?: number }) =>
      ['tenant', slug, 'reporting', 'control-assessments', filters] as const,
    controlsSummary: (slug: string) =>
      ['tenant', slug, 'reporting', 'controls-summary'] as const,
  },
} as const
